/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { isArray } from 'lodash';
import { useComponent } from '../../../Pages/UserSetting/UsersLibrary/UserRights';
import { RightsAccess } from '../../helper';
import MenuItem from '../MenuItem';
import { getModuleOrderIconComponent } from '../../../constants/module-order-icon-registry';
import { getLibraryCardMenu, getSystemCanvasModuleMenu } from '../../../Pages/DevlopmentOrder/store/DevelopmentOrder.action';
import { PRODUCTS_MENU_ITEM_MAP, normalizeItemName } from '../../../constants/module-order-menu-mapping';
import { getModuleOrderItemsForProduct } from '../../../services/SystemMaster.services';
import {
  BillOfMaterialsIcon,
  CareUserInstructionsIcon,
  CostingsIcon,
  CustomModulesIcon,
  DAMIcon,
  DesignDetailsIcon,
  DPPLibraryIcon,
  MeasurementsIcon,
  ProductIdentifierIcon,
  PurchaseOrdersIcon,
  SamplesIcon,
  TestingIcon,
} from '../../../bombiix-icon';

/** Prefixes the module-order API uses to namespace System Canvas and DPP item ids. */
const PREFIX_SYSTEM_CANVAS = 'sc-';
const PREFIX_DPP = 'dpp-';

/**
 * True when the stored icon is an absolute URL rather than an icon-font class name.
 * @param {unknown} icon stored icon value
 * @returns {boolean} whether the value should be rendered as an image URL
 */
function isIconUrl(icon) {
  if (icon == null || typeof icon !== 'string') return false;
  const trimmed = icon.trim();
  return trimmed.length > 0 && (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//'));
}

/**
 * Splits a stored icon value into the props MenuItem expects, falling back to a
 * class name when the value is not a URL.
 * @param {unknown} icon stored icon value
 * @param {string} fallbackClass icon-font class used when no URL is present
 * @returns {{ iconClass: string, iconUrl: string | undefined }} MenuItem icon props
 */
function getIconDisplayProps(icon, fallbackClass = 'icon-Inside-Custom-Modules') {
  const useAsUrl = isIconUrl(icon);
  const urlValue = useAsUrl && typeof icon === 'string' ? icon.trim() : undefined;
  return {
    iconClass: useAsUrl ? fallbackClass : icon || fallbackClass,
    iconUrl: urlValue || undefined,
  };
}

function DevorderUrl(props) {
  const { CheckModulePermission } = useComponent();
  const reducer = useSelector((reducerstate) => reducerstate);
  const dispatch = useDispatch();

  const {
    BOMConfigDetails: { trafficLights, productDetails },
    Users: { logInRights, moduleAccess },
    UserDetails: { isSupplierLoggedIn, isCompanyAdminLoggedIn },
    Measuremets: { customModuleMenu, customModuleLoaderState, dppModuleMenu, systemCanvasModuleMenu = [] },
    SystemMastersDetails: { moduleOrderConfigs = [] },
  } = reducer;
  const { isMobileMenu } = props;
  const DesignAccesss = CheckModulePermission(logInRights, 'products', 'design', isCompanyAdminLoggedIn);
  const MeasurementAccess = CheckModulePermission(logInRights, 'products', 'measurement', isCompanyAdminLoggedIn);
  const BomAccess = CheckModulePermission(logInRights, 'products', 'bom', isCompanyAdminLoggedIn);
  const TestingAccess = CheckModulePermission(logInRights, 'products', 'testing', isCompanyAdminLoggedIn);
  const CareAccess = CheckModulePermission(logInRights, 'products', 'care', isCompanyAdminLoggedIn);
  const SampleAccess = CheckModulePermission(logInRights, 'products', 'samples', isCompanyAdminLoggedIn);
  const customModuleAccess = CheckModulePermission(logInRights, 'products', 'customModule', isCompanyAdminLoggedIn);
  const ProductCostingAccess = CheckModulePermission(logInRights, 'products', 'costing', isCompanyAdminLoggedIn);
  const PurchaseOrderAccess = CheckModulePermission(logInRights, 'products', 'purchaseOrder', isCompanyAdminLoggedIn);
  const DAMAccesss = CheckModulePermission(logInRights, 'products', 'DAM', isCompanyAdminLoggedIn);
  const ProductIdentifierAccess = CheckModulePermission(logInRights, 'products', 'productIdentifier', isCompanyAdminLoggedIn);

  const { dppAccess } = moduleAccess;
  const ids = useLocation();
  const { productCostingRight, purchaseOrderRight, customModuleRight, DAMRight, DPPRight, TestingRight } = RightsAccess();
  const devOrderId = ids.pathname.match(/\/development-order\/([0-9a-f]{24})/i)?.[1];

  const history = useHistory();
  const pathName = history.location.pathname;

  const trafficLightClassNames = (property) => {
    return trafficLights && property ? `colored-cls ${property}` : 'colored-cls';
  };
  const trafficLightCustomModuleClassNames = (libId) => {
    const colour = trafficLights?.additionalInformation?.filter((item) => item?.customLibId === libId && item !== null).map((item) => item.color);

    return `colored-cls ${isArray(colour) ? colour[0] : ''}`;
  };

  const accessByPermissionKey = useMemo(
    () => ({
      productsAccess: moduleAccess?.productsAccess,
      design: DesignAccesss,
      measurement: MeasurementAccess,
      bom: BomAccess,
      testing: TestingAccess,
      care: CareAccess,
      samples: SampleAccess,
      costing: ProductCostingAccess,
      purchaseOrder: PurchaseOrderAccess,
      DAM: DAMAccesss,
      productIdentifier: moduleAccess?.productsAccess && ProductIdentifierAccess,
    }),
    [
      moduleAccess?.productsAccess,
      DesignAccesss,
      MeasurementAccess,
      BomAccess,
      TestingAccess,
      CareAccess,
      SampleAccess,
      ProductCostingAccess,
      PurchaseOrderAccess,
      DAMAccesss,
      ProductIdentifierAccess,
    ],
  );

  const rightsByKey = useMemo(
    () => ({
      TestingRight,
      productCostingRight,
      purchaseOrderRight,
      DAMRight,
    }),
    [TestingRight, productCostingRight, purchaseOrderRight, DAMRight],
  );

  const [productOrderItems, setProductOrderItems] = useState([]);
  const productsConfig = useMemo(() => (moduleOrderConfigs || []).find((c) => c.moduleId === 'products'), [moduleOrderConfigs]);
  const orderedStaticItems = useMemo(
    () => (productsConfig?.items ? [...productsConfig.items].filter((i) => i.isActive !== false).sort((a, b) => (a.sequence || 0) - (b.sequence || 0)) : null),
    [productsConfig],
  );

  const canShowProductItem = (routeConfig) => {
    const access = accessByPermissionKey[routeConfig.permissionKey];
    if (!access) return false;
    if (routeConfig.rightKey && !rightsByKey[routeConfig.rightKey]) return false;
    if (routeConfig.permissionKey === 'DAM' && isSupplierLoggedIn) return false;
    return true;
  };

  const sequencedStaticItems = useMemo(() => {
    if (!orderedStaticItems || !devOrderId) return [];
    return orderedStaticItems
      .map((item) => {
        const key = normalizeItemName(item.itemName);
        const routeConfig = PRODUCTS_MENU_ITEM_MAP[key];
        if (!routeConfig || !canShowProductItem(routeConfig)) return null;
        const linkTo = `/development-order/${devOrderId}/${routeConfig.pathSuffix}`;
        const trafficLightStyle = routeConfig.trafficLightKey ? trafficLightClassNames(trafficLights?.[routeConfig.trafficLightKey]) : undefined;
        return (
          <MenuItem
            key={item.itemId || key}
            activeClassList={routeConfig.activeClassList}
            pathName={pathName}
            linkTo={linkTo}
            IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? routeConfig.iconClass)}
            iconClass={routeConfig.iconClass}
            titleKey={routeConfig.titleKey}
            trafficLightStyle={trafficLightStyle}
            isMobileMenu={isMobileMenu}
          />
        );
      })
      .filter(Boolean);
  }, [orderedStaticItems, devOrderId, pathName, isMobileMenu, trafficLights, accessByPermissionKey, rightsByKey, isSupplierLoggedIn]);

  useEffect(() => {
    const fetchSystemCanvasModules = async () => {
      if (!devOrderId) return;
      await dispatch(getSystemCanvasModuleMenu({ devOrderId }));
      await dispatch(getLibraryCardMenu({ devOrderId }));
    };

    fetchSystemCanvasModules();
  }, [devOrderId, dispatch]);

  useEffect(() => {
    const fetchProductModuleOrder = async () => {
      if (!devOrderId) return;
      try {
        const res = await getModuleOrderItemsForProduct({ moduleId: 'products', devOrderId });
        const list = res?.code === 200 && Array.isArray(res?.data) ? res.data : [];
        setProductOrderItems(list);
      } catch {
        setProductOrderItems([]);
      }
    };
    fetchProductModuleOrder();
  }, [devOrderId]);

  const sequencedProductMenuItems = useMemo(() => {
    if (!devOrderId || !productOrderItems?.length) return null;
    return productOrderItems
      .map((item) => {
        const idStr = String(item._id ?? item.itemId ?? '');
        const refId = item.itemReferenceId ?? idStr;
        const isSc = item.itemType === 'sc' || (item.itemType !== 'dpp' && item.itemType !== 'cm' && idStr.startsWith(PREFIX_SYSTEM_CANVAS));
        const isDpp = item.itemType === 'dpp' || (item.itemType !== 'sc' && item.itemType !== 'cm' && idStr.startsWith(PREFIX_DPP));
        const moduleId = refId.startsWith(PREFIX_SYSTEM_CANVAS) ? refId.slice(PREFIX_SYSTEM_CANVAS.length) : refId;
        const dppRefId = refId.startsWith(PREFIX_DPP) ? refId.slice(PREFIX_DPP.length) : refId;

        const iconValue = item.icon ?? item.iconUrl ?? (typeof item.icon === 'object' && item.icon?.url ? item.icon.url : null);
        const dynamicIconProps = getIconDisplayProps(iconValue, 'icon-Inside-Custom-Modules');

        if (isSc) {
          if (!customModuleRight || !customModuleAccess) return null;
          return (
            <MenuItem
              key={idStr}
              activeClassList={['/custom-module/system-canvas/']}
              activeId={moduleId}
              pathName={pathName}
              linkTo={`/development-order/${devOrderId}/custom-module/system-canvas/${moduleId}`}
              IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? dynamicIconProps.iconClass)}
              iconClass={dynamicIconProps.iconClass}
              iconUrl={dynamicIconProps.iconUrl}
              titleKey={item.name || item.itemName}
              trafficLightStyle={trafficLightCustomModuleClassNames(moduleId)}
              isMobileMenu={isMobileMenu}
            />
          );
        }
        if (isDpp) {
          if (!dppAccess || !DPPRight || !moduleAccess?.productsAccess) return null;
          const dppIconValue = item.icon ?? item.iconUrl ?? (typeof item.icon === 'object' && item.icon?.url ? item.icon.url : null);
          const dppIconProps = getIconDisplayProps(dppIconValue, 'icon-Inside-Digital-Product-Passports');
          return (
            <MenuItem
              key={idStr}
              activeClassList={['dpp-module']}
              activeId={dppRefId}
              pathName={pathName}
              linkTo={`/development-order/${devOrderId}/dpp-module/${dppRefId}`}
              IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? dppIconProps.iconClass)}
              iconClass={dppIconProps.iconClass}
              iconUrl={dppIconProps.iconUrl}
              titleKey={item.name}
              trafficLightStyle={trafficLightCustomModuleClassNames(dppRefId)}
              isMobileMenu={isMobileMenu}
            />
          );
        }
        if (item.itemType === 'cm') {
          if (!customModuleRight || !customModuleAccess) return null;
          return (
            <MenuItem
              key={idStr}
              activeClassList={['/custom-module/']}
              activeId={refId}
              pathName={pathName}
              linkTo={`/development-order/${devOrderId}/custom-module/${refId}`}
              IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? dynamicIconProps.iconClass)}
              iconClass={dynamicIconProps.iconClass}
              iconUrl={dynamicIconProps.iconUrl}
              titleKey={item.name || item.itemName}
              trafficLightStyle={trafficLightCustomModuleClassNames(refId)}
              isMobileMenu={isMobileMenu}
            />
          );
        }
        if (item.itemType === 'cl') {
          return (
            <MenuItem
              key={idStr}
              activeClassList={['/control-library/']}
              activeId={refId}
              pathName={pathName}
              linkTo={`/development-order/${devOrderId}/control-library/${refId}`}
              IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? dynamicIconProps.iconClass)}
              iconClass={dynamicIconProps.iconClass}
              iconUrl={dynamicIconProps.iconUrl}
              titleKey={item.name || item.itemName}
              isMobileMenu={isMobileMenu}
            />
          );
        }
        const key = normalizeItemName(item.name || item.itemName);
        const routeConfig = PRODUCTS_MENU_ITEM_MAP[key];
        if (!routeConfig || !canShowProductItem(routeConfig)) return null;
        const linkTo = `/development-order/${devOrderId}/${routeConfig.pathSuffix}`;
        const trafficLightStyle = routeConfig.trafficLightKey ? trafficLightClassNames(trafficLights?.[routeConfig.trafficLightKey]) : undefined;
        const staticIconValue = item.icon ?? item.iconUrl ?? (typeof item.icon === 'object' && item.icon?.url ? item.icon.url : null);
        const staticIconProps = getIconDisplayProps(staticIconValue, routeConfig.iconClass);
        return (
          <MenuItem
            key={item.itemId || idStr || key}
            activeClassList={routeConfig.activeClassList}
            pathName={pathName}
            linkTo={linkTo}
            IconComponent={getModuleOrderIconComponent(item.iconName ?? item.icon ?? routeConfig.iconClass)}
            iconClass={staticIconProps.iconClass}
            iconUrl={staticIconProps.iconUrl}
            titleKey={routeConfig.titleKey}
            trafficLightStyle={trafficLightStyle}
            isMobileMenu={isMobileMenu}
          />
        );
      })
      .filter(Boolean);
  }, [
    devOrderId,
    productOrderItems,
    pathName,
    isMobileMenu,
    trafficLights,
    dppAccess,
    DPPRight,
    moduleAccess?.productsAccess,
    accessByPermissionKey,
    rightsByKey,
    customModuleRight,
    customModuleAccess,
    isSupplierLoggedIn,
  ]);

  const hasSequencedItems = sequencedProductMenuItems?.length > 0 || sequencedStaticItems.length > 0;
  const useProductOrder = sequencedProductMenuItems != null && sequencedProductMenuItems.length > 0;


  return (
    !customModuleLoaderState && (
      <ul>
        {/*
          Module order drives the rail whenever System Master has a saved Products
          config: sequencedProductMenuItems is the order list intersected with the
          System Canvas / custom module / DPP / control-library entries that apply to
          this product. The hardcoded list below is the fallback for tenants with no
          saved config, and was previously the only thing rendered — the sequenced
          items were computed and discarded, so configured order and every dynamic
          module were invisible.
        */}
        {hasSequencedItems ? (
          <>
            {sequencedProductMenuItems}
            {sequencedProductMenuItems == null && sequencedStaticItems}
          </>
        ) : (
          <>
        {moduleAccess?.productsAccess && ProductIdentifierAccess && (
          <MenuItem
            activeClassList={['/product-identifier']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/product-identifier`}
            IconComponent={ProductIdentifierIcon}
            titleKey="DEVORDER_URL.PRODUCT_IDENTIFIER"
            isMobileMenu={isMobileMenu}
          />
        )}
        {DesignAccesss && (
          <MenuItem
            activeClassList={['/design-details']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/design-details/list`}
            IconComponent={DesignDetailsIcon}
            titleKey="DEVORDER_URL.DESIGN_DETAILS"
            trafficLightStyle={trafficLightClassNames(trafficLights?.design)}
            isMobileMenu={isMobileMenu}
          />
        )}
        {BomAccess && (
          <MenuItem
            activeClassList={['/bom', '/bom-type']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/bom`}
            IconComponent={BillOfMaterialsIcon}
            titleKey="DEVORDER_URL.BOM"
            trafficLightStyle={trafficLightClassNames(trafficLights?.bom)}
            isMobileMenu={isMobileMenu}
          />
        )}

        {MeasurementAccess && (
          <MenuItem
            activeClassList={['/measurement']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/measurement`}
            IconComponent={MeasurementsIcon}
            titleKey="DEVORDER_URL.SPECIFICATIONS"
            trafficLightStyle={trafficLightClassNames(trafficLights?.measurement)}
            isMobileMenu={isMobileMenu}
          />
        )}
        {TestingAccess && TestingRight && (
          <MenuItem
            activeClassList={['/testing', 'testing-group']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/testing/testing-requirements`}
            IconComponent={TestingIcon}
            titleKey="DEVORDER_URL.TESTING"
            trafficLightStyle={trafficLightClassNames(trafficLights?.testing)}
            isMobileMenu={isMobileMenu}
          />
        )}

        {CareAccess && (
          <MenuItem
            activeClassList={['care-user-instruction']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/care-user-instruction`}
            IconComponent={CareUserInstructionsIcon}
            titleKey="DEVORDER_URL.CARE_USER_INSTRUCTIONS"
            trafficLightStyle={trafficLightClassNames(trafficLights?.care)}
            isMobileMenu={isMobileMenu}
          />
        )}
        {SampleAccess && (
          <MenuItem
            activeClassList={['sample']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/samples`}
            IconComponent={SamplesIcon}
            titleKey="DEVORDER_URL.SAMPLES"
            trafficLightStyle={trafficLightClassNames(trafficLights?.samples)}
            isMobileMenu={isMobileMenu}
          />
        )}

        {customModuleRight &&
          customModuleAccess &&
          customModuleMenu?.length > 0 &&
          customModuleMenu?.map((ele) => (
            <MenuItem
              key={ele}
              activeClassList={['/custom-module/']}
              activeId={ele?._id}
              pathName={pathName}
              linkTo={`/development-order/${devOrderId}/custom-module/${ele?._id}`}
              iconClass="icon-Inside-Custom-Modules"
              titleKey={ele.moduleName}
              trafficLightStyle={trafficLightCustomModuleClassNames(ele?._id)}
              isMobileMenu={isMobileMenu}
            />
          ))}
        {!useProductOrder &&
          customModuleRight &&
          customModuleAccess &&
          systemCanvasModuleMenu &&
          systemCanvasModuleMenu.length > 0 &&
          systemCanvasModuleMenu.map((ele) => {
            const moduleId = ele?.moduleId || ele?._id;
            const moduleKey = ele?.source === 'DevelopmentOrdersModules' ? ele?.moduleId : ele?._id;
            return (
              <MenuItem
                key={moduleKey}
                activeClassList={['/custom-module/system-canvas/']}
                activeId={moduleId}
                pathName={pathName}
                linkTo={`/development-order/${devOrderId}/custom-module/${ele?._id}`}
                IconComponent={CustomModulesIcon}
                titleKey={ele.moduleName}
                trafficLightStyle={trafficLightCustomModuleClassNames(ele?._id)}
                isMobileMenu={isMobileMenu}
              />
            );
          })}
        {/* Control libraries are rendered via moduleOrderConfig items (itemType === 'cl') above */}
        {!useProductOrder &&
dppAccess &&
          DPPRight &&
          moduleAccess?.productsAccess &&
          dppModuleMenu?.length > 0 &&
          dppModuleMenu?.map((ele) => {
            return (
              <MenuItem
                key={ele}
                activeClassList={['dpp-module']}
                activeId={ele?._id}
                pathName={pathName}
                linkTo={`/development-order/${devOrderId}/dpp-module/${ele?._id}`}
                IconComponent={DPPLibraryIcon}
                titleKey={ele.dppName}
                trafficLightStyle={trafficLightCustomModuleClassNames(ele?._id)}
                isMobileMenu={isMobileMenu}
              />
            );
          })}

        {ProductCostingAccess && productCostingRight && (
          <MenuItem
            activeClassList={['product-costing']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/product-costing`}
            IconComponent={CostingsIcon}
            titleKey="DEVORDER_URL.PRODUCT_COSTING"
            trafficLightStyle={trafficLightClassNames(trafficLights?.costing)}
            isMobileMenu={isMobileMenu}
          />
        )}
        {PurchaseOrderAccess && purchaseOrderRight && (
          <MenuItem
            activeClassList={['product-purchase-order']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/product-purchase-order`}
            IconComponent={PurchaseOrdersIcon}
            titleKey="DEVORDER_URL.PURCHASE_ORDERS"
            trafficLightStyle={trafficLightClassNames(trafficLights?.po)}
            isMobileMenu={isMobileMenu}
          />
        )}
        {DAMAccesss && DAMRight && !isSupplierLoggedIn && (
          <MenuItem
            activeClassList={['digital-assets-management']}
            pathName={pathName}
            linkTo={`/development-order/${devOrderId}/digital-assets-management`}
            IconComponent={DAMIcon}
            titleKey="DEVORDER_URL.DAM"
            isMobileMenu={isMobileMenu}
          />
        )}
          </>
        )}
      </ul>
    )
  );
}

export default DevorderUrl;
